import { useEffect, useMemo, useState } from "react";
import "./FileSystemControls.scss";
import { CARD_NAMES, GET_DATA_CONTROL_CARD_SUMMARY, GET_DATA_CONTROL_CARD_SUMMARY_COUNT, GET_DATA_CONTROL_DASHBOARD_SUMMARY, RISK_CATEGORIES, actionKey } from "./DataControlConstans";
import {
  AlertStatusButton,
  RecommendedActionsPanel,
} from "./components/AlertsPanel";
import { RiskOverviewHeader } from "./components/DataConstolRiskHeader";
import { RiskDeck } from "./components/DataControlCarouselCard";
import { RiskFullScreen } from "./components/DataControlRiskTable";
import { ConfirmModal } from "./DataControlUtils";
import { useDispatch, useSelector } from "react-redux";
import SnackBar2 from "../../components/shared/SnackBar2";

export default function FileSystemControls() {
  const dispatch = useDispatch();
  const [modal, setModal] = useState({
    open: false,
    message: "",
    onConfirm: null,
  });

  const snackBarMessage = useSelector((state) => state.dataControlReducer.dataControlSnackbar);

  // recommended actions (topbar pill)
  const [actionsPanelOpen, setActionsPanelOpen] = useState(false);
  const [highlightedItem, setHighlightedItem] = useState(null);

  // every applied action lives in one place, keyed by category+subject, so
  // the overview header, the deck cards, the stat cards, the item list, and
  // the log table can all read the same source of truth and stay in sync.
  const [appliedActions, setAppliedActions] = useState({});

  // which category is open — drives RiskDeck between its fanned and
  // stacked layouts; the header/gauge below never unmounts, only the deck
  // itself recomputes each card's target transform.
  const [openRiskId, setOpenRiskId] = useState(null);
  const openRisk = RISK_CATEGORIES.find((c) => c.id === openRiskId) || null;
  console.log(openRiskId, "openRiskId",);

  useEffect(() => {
    if (!highlightedItem || !openRiskId) return;

    const timer = setTimeout(() => {
      const row = document.getElementById(`risk-row-${highlightedItem}`);

      if (row) {
        row.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        row.classList.add("fsc-risk-item--highlight");

        setTimeout(() => {
          row.classList.remove("fsc-risk-item--highlight");
          setHighlightedItem(null);
        }, 2000);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [openRiskId, highlightedItem]);

  useEffect(() => {
    async function loadCards() {
      for (const cardName of CARD_NAMES) {
        dispatch({
          type: GET_DATA_CONTROL_CARD_SUMMARY,
          payload: { cardName },
        });
        dispatch({
          type: GET_DATA_CONTROL_CARD_SUMMARY_COUNT,
          payload: { cardName },
        });

        // small delay so backend finishes auth/token work
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
      dispatch({
        type: GET_DATA_CONTROL_DASHBOARD_SUMMARY,
      });
    }
    loadCards();
  }, [dispatch]);

  function openRiskFromAction(action) {
    setOpenRiskId(action.categoryId);
    setHighlightedItem(action.subject);
    setActionsPanelOpen(false);
  }

  // used by the fanned card's "Recommended Action" button — opens the
  // stack + detail split view for that category and scrolls straight to
  // the flagged item
  function openCategoryAction(categoryId, subject) {
    setOpenRiskId(categoryId);
    setHighlightedItem(subject);
  }

  function askRiskAction(item, actionLabel, dispatch) {
    const getTitle = () => {
      if (openRiskId === "public-sensitive") {
        return item.title;
      } else if (openRiskId === "user-risk") {
        return item.userName;
      } else if (openRiskId === "org-wide") {
        return item.dataSourceType;
      } else {
        return item.userName;
      }
    }
    setModal({
      open: true,
      message: `Apply "${actionLabel}" to ${getTitle()}?`,
      onConfirm: () => {
        if (openRiskId === "public-sensitive") {
          dispatch({
            type: "REVOKE_FILE_ACCESS",
            payload: {
              fileIds: item.sensitivityId,
              dataSourceId: item.dataSourceId,
              openRiskId: openRiskId,
            },
          });
        }
        if (openRiskId === "user-risk") {
          dispatch({
            type: "REVOKE_FILE_ACCESS",
            payload: {
              fileIds: item.userId,
              dataSourceId: item.dataSourceId,
              openRiskId: openRiskId,
            },
          });
        }
        if (openRiskId === "org-wide") {
          dispatch({
            type: "REVOKE_FILE_ACCESS",
            payload: {
              fileIds: null,
              dataSourceId: item.dataSourceId,
              openRiskId: openRiskId,
            },
          });
        } 
        if (openRiskId === "location") {
          dispatch({
            type: "REVOKE_FILE_ACCESS",
            payload: {
              fileIds: item.userId,
              dataSourceId: item.dataSourceId,
              openRiskId: openRiskId,
            },
          });
        }
        setAppliedActions((prev) => ({
          ...prev,
          [actionKey(openRiskId, item.subject)]: actionLabel,
        }));
        setModal((m) => ({ ...m, open: false }));
      },
    });
  }

  // flat list of every not-yet-applied item across all categories, most
  // urgent first — drives the topbar pill, the dropdown, and category stats
  const allPendingItems = useMemo(() => {
    return RISK_CATEGORIES.flatMap((category) =>
      category.items
        .filter((item) => !appliedActions[actionKey(category.id, item.subject)])
        .map((item) => ({
          id: actionKey(category.id, item.subject),
          categoryId: category.id,
          icon: category.icon,
          iconVariant: category.severity,
          title: item.subject,
          description: item.reason,
          cta: item.actions[0],
          subject: item.subject,
          priority: category.priority?.toLowerCase(),
        })),
    ).sort((a, b) => {
      const order = { critical: 4, high: 3, medium: 2, low: 1 };
      return order[b.priority] - order[a.priority];
    });
  }, [appliedActions]);

  const pendingCount = allPendingItems.length;

  return (
    <div className="fsc">
      {/* Topbar — never touched by anything below it. Title is intentionally
          fixed as "Data Controls" at all times (fan view AND stack/detail
          view) — it used to swap to the open category's name, which made
          the page feel like it had navigated somewhere else even though
          the header/gauge underneath never actually moved. */}
      <header className="fsc-topbar">
        <div className="fsc-topbar__title-block">
          {openRisk ? (
            <button
              className="fsc-topbar__back"
              onClick={() => setOpenRiskId(null)}
            >
              <i className="ti ti-arrow-left" />
            </button>
          ) : (
            <div className="fsc-topbar__title-icon">
              <i className="ti ti-lock" />
            </div>
          )}
          <h1 className="fsc-topbar__title">Data Control</h1>
        </div>
        <div className="fsc-topbar__right">
          <div className="fsc-actions-anchor">
            {/* <AlertStatusButton
              pendingCount={pendingCount}
              onClick={() => setActionsPanelOpen((o) => !o)}
            /> */}
            {/* {actionsPanelOpen && (
              <RecommendedActionsPanel
                actions={allPendingItems}
                onResolve={openRiskFromAction}
                onClose={() => setActionsPanelOpen(false)}
              />
            )} */}
          </div>
          {/* <span className="fsc-topbar__time">
            <i className="ti ti-refresh" /> Last Updated 8:12:26
          </span> */}
        </div>
      </header>

      <main className="fsc-content">
        <RiskOverviewHeader appliedActions={appliedActions} />

        <RiskDeck
          categories={RISK_CATEGORIES}
          openRiskId={openRiskId}
          onSelect={setOpenRiskId}
          onOpenAction={openCategoryAction}
          detail={
            openRisk && (
              <RiskFullScreen
                category={openRisk}
                appliedActions={appliedActions}
                onAction={askRiskAction}
              />
            )
          }
        />
      </main>

      {/* Confirm modal */}
      <ConfirmModal
        isOpen={modal.open}
        message={modal.message}
        onConfirm={modal.onConfirm}
        onCancel={() => setModal((m) => ({ ...m, open: false }))}
      />
      <SnackBar2
        success={snackBarMessage.success}
        failure={snackBarMessage.failure}
        successMessage={snackBarMessage.successMessage}
        errorMessage={snackBarMessage.failureMessage}
      ></SnackBar2>
    </div>
  );
}